"use client"

import { useState, useEffect } from "react"
import { useTheme } from "../../contexts/ThemeContext"
import { useAuth } from "../../contexts/AuthContext"
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ChevronDownIcon,
  UserIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"

const TopBar = ({ onToggleSidebar, onToggleMobileSidebar }) => {
  const { isDark, toggleTheme } = useTheme()
  const { user, logout, getUserBalance, getSupportNotifications, markSupportNotificationsRead } = useAuth()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [balance, setBalance] = useState(0)

  const fetchNotifications = async () => {
    try {
      const result = await getSupportNotifications(false)
      if (result.success) {
        setNotifications(result.data || [])
        setUnreadCount(result.unreadCount || 0)
      }
    } catch (error) {
      console.error("Error loading notifications:", error)
    }
  }

  useEffect(() => {
    const fetchBalance = async () => {
      const result = await getUserBalance()
      if (result.success) {
        setBalance(result.balance)
      }
    }
    if (user) {
      fetchBalance()
      fetchNotifications()
      const interval = setInterval(fetchNotifications, 30000)
      return () => clearInterval(interval)
    }
  }, [user, getUserBalance])

  const handleNotificationClick = async () => {
    setUserMenuOpen(false)
    setNotificationMenuOpen(!notificationMenuOpen)
    if (!notificationMenuOpen) {
      try {
        await markSupportNotificationsRead()
        setUnreadCount(0)
      } catch (error) {
        console.error("Error marking notifications read:", error)
      }
    }
  }

  const handleSignOut = () => {
    logout()
    window.location.href = "/login" // redirect to login after logout
  }

  const openProfile = (tab = "") => {
    const userId = user?._id || user?.id
    if (!userId) return
    setUserMenuOpen(false)
    window.location.href = `/userprofile/${userId}${tab ? `?tab=${tab}` : ""}`
  }

  return (
    <header className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50 px-6 py-4 sticky top-0 z-40 shadow-sm">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onToggleMobileSidebar}
            className="lg:hidden p-3 rounded-2xl transition-all duration-300"
            style={{ background: "linear-gradient(135deg, rgba(74,124,89,0.1), rgba(255,215,0,0.1))" }}
          >
            <Bars3Icon className="w-5 h-5 text-black dark:text-white" />
          </button>

          <button
            onClick={onToggleSidebar}
            className="hidden lg:block p-3 rounded-2xl transition-all duration-300"
            style={{ background: "linear-gradient(135deg, rgba(74,124,89,0.1), rgba(255,215,0,0.1))" }}
          >
            <Bars3Icon className="w-5 h-5 text-black dark:text-white" />
          </button>

          {/* Search */}
          <div className="relative hidden md:block">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-black dark:text-white" />
            </div>
            <input
              type="text"
              placeholder="Search or type command..."
              className="
                w-96 pl-12 pr-16 py-3 
                rounded-2xl text-sm
                text-black dark:text-white 
                placeholder-gray-500 dark:placeholder-gray-400 
                focus:outline-none focus:ring-2 focus:ring-green-500/50 
                focus:border-green-500/50 backdrop-blur-sm transition-all duration-300

                bg-gradient-to-r from-green-50/80 to-yellow-50/80    /* light mode */
                dark:bg-gradient-to-r dark:from-gray-800/90 dark:to-gray-900/90 /* dark mode */
                border border-green-200/50 dark:border-green-700/50
              "
            />
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-3">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-3 rounded-2xl transition-all duration-300 group"
            style={{ background: "linear-gradient(135deg, rgba(74,124,89,0.1), rgba(255,215,0,0.1))" }}
          >
            {isDark ? (
              <SunIcon className="w-5 h-5 text-black dark:text-white transition-colors duration-300" />
            ) : (
              <MoonIcon className="w-5 h-5 text-black dark:text-white transition-colors duration-300" />
            )}
          </button>

          {/* Notification */}
          <div className="relative">
            <button
              onClick={handleNotificationClick}
              className="p-3 rounded-2xl relative transition-all duration-300 group"
              style={{ background: "linear-gradient(135deg, rgba(74,124,89,0.1), rgba(255,215,0,0.1))" }}
            >
              <BellIcon className="w-5 h-5 text-black dark:text-white transition-colors duration-300" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse shadow-lg"></span>
              )}
            </button>

            {notificationMenuOpen && (
              <div className="absolute right-0 mt-3 w-80 md:w-96 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-xl border border-green-200/50 dark:border-green-800/50 py-3 z-50 animate-in slide-in-from-top-2 duration-300">
                <div className="px-5 py-3 border-b border-green-200/50 dark:border-green-800/50 flex justify-between items-center">
                  <span className="font-semibold text-black dark:text-white">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded-full font-medium">
                      {unreadCount} new
                    </span>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                  {notifications.length === 0 ? (
                    <div className="px-5 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div
                        key={notif._id}
                        onClick={() => {
                          setNotificationMenuOpen(false)
                          window.location.href = "/support"
                        }}
                        className={`px-5 py-3.5 hover:bg-green-50/50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors duration-200 ${
                          !notif.isRead ? "bg-green-50/20 dark:bg-green-950/10" : ""
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm text-black dark:text-white">
                            {notif.title}
                          </span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-500 whitespace-nowrap ml-2">
                            {new Date(notif.createdAt).toLocaleDateString() === new Date().toLocaleDateString()
                              ? new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : new Date(notif.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                          {notif.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="px-5 pt-3 border-t border-green-200/50 dark:border-green-800/50 text-center">
                  <button
                    onClick={() => {
                      setNotificationMenuOpen(false)
                      window.location.href = "/support"
                    }}
                    className="text-xs text-green-600 dark:text-green-400 font-semibold hover:underline"
                  >
                    View support workspace
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          {user && (
            <div className="relative">
              <button
                onClick={() => {
                  setNotificationMenuOpen(false)
                  setUserMenuOpen(!userMenuOpen)
                }}
                className="flex items-center space-x-3 p-2 rounded-2xl transition-all duration-300"
                style={{ background: "linear-gradient(135deg, rgba(74,124,89,0.1), rgba(255,215,0,0.1))" }}
              >
                <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-yellow-500 rounded-full flex items-center justify-center shadow-sm">
                  <span className="text-white font-semibold text-sm">
                    {user.username ? user.username.charAt(0).toUpperCase() : "U"}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-semibold text-black dark:text-white">{user.username}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{user.role}</div>
                </div>
                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform duration-300 ${
                    userMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-3 w-72 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-1xl border border-green-200/50 dark:border-green-800/50 py-3 z-50 animate-in slide-in-from-top-2 duration-300">
                  <div className="px-5 py-4 border-b border-green-200/50 dark:border-green-800/50">
                    <div className="font-semibold text-black dark:text-white">{user.username}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{user.email}</div>
                    <div className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Balance: <strong>{typeof balance === "number" ? balance.toFixed(2) : Number(balance || 0).toFixed(2)}</strong>
                    </div>
                  </div>

                  <div className="py-2">
                    <button
                      onClick={() => openProfile()}
                      className="w-full flex items-center px-5 py-3 text-sm text-black dark:text-white group rounded-xl mx-2"
                    >
                      <UserIcon className="w-5 h-5 mr-3" />
                      Edit profile
                    </button>
                    <button
                      onClick={() => openProfile("settings")}
                      className="w-full flex items-center px-5 py-3 text-sm text-black dark:text-white group rounded-xl mx-2"
                    >
                      <Cog6ToothIcon className="w-5 h-5 mr-3" />
                      Account settings
                    </button>
                    <button
                      onClick={() => openProfile("security")}
                      className="w-full flex items-center px-5 py-3 text-sm text-black dark:text-white group rounded-xl mx-2"
                    >
                      <ShieldCheckIcon className="w-5 h-5 mr-3" />
                      Security
                    </button>
                    <button className="w-full flex items-center px-5 py-3 text-sm text-black dark:text-white group rounded-xl mx-2">
                      <QuestionMarkCircleIcon className="w-5 h-5 mr-3" />
                      Support
                    </button>
                  </div>

                  <div className="border-t border-green-200/50 dark:border-green-800/50 pt-2">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center px-5 py-3 text-sm text-red-600 dark:text-red-400 group rounded-xl mx-2"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default TopBar
